import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { Observable, firstValueFrom } from 'rxjs';
import { shareReplay } from 'rxjs/operators';

// services
import { AuthService } from './auth.service';
import { FirebaseService } from './firebase.service';
import { StorageManagerService } from './storage-manager.service';
import { LocalStorageService } from './local-storage.service';
import { getSurveyFromStorageWithFallback } from '../utils/survey-storage';
// models
import { Survey } from '../models/survey';
import { Partial_answers } from '../models/answer';
import { NetworkService } from './network.service';

@Injectable({
  providedIn: 'root'
})
export class QservusApiService {

  apiQSSUrl: string = environment.qsApiQSSUrl;
  apiQSUrl: string = environment.qsApiUrl;
  uuid4: string = "";
  private sensorsCache$: Observable<unknown> | null = null;
  private sensorsCacheExpiry = 0;
  private readonly sensorsCacheTtlMs = 5 * 60 * 1000;

  json_test = {
    all_questions: "assets/test/survey_test_all_type_questions.json",
    checklist_entrega_dedicada: "assets/test/checklist_entrega_dedicada.json",
    checklist_asistente_operaciones: "assets/test/checklist_asistente_operaciones.json",
    checklist_atencion_ejecutivo: "assets/test/checklist_atencion_ejecutivo.json",
    logic_to_show: "assets/test/logica_para_mostrar.json"
  }
	api_domain: string;

  constructor(
    private http: HttpClient,
    private authService: AuthService,
    private firebaseService: FirebaseService, 
    private networkService: NetworkService,
    private storageManagerService: StorageManagerService,
    private localStorageService: LocalStorageService) {
  }

  /**
   * @author Matías Villar
   * @date 29-12-2022
   * @desription get survey by uuid4
   * @param survey_uuid4 uuid4 of survey 
   * @returns 
   */
  getSurvey(survey_uuid4: string): Promise<Survey> {
    return firstValueFrom(this.http.get<Survey>(environment.qsApiQSSUrl + "surveys/" + survey_uuid4 + "/", this.getHeaders()));
  }

  /**
   * @author Matías Villar
   * @date 29-12-2022
   * @desription get conjoin data from assets/test/ folder
   * @param 
   * @returns 
   */
  async getTestConjoint(): Promise<Survey> {
    return await firstValueFrom(this.http.get<Survey>("assets/test/survey_conjoint.json"));
  }

  /**
   * @author Matías Villar
   * @date 29-12-2022
   * @desription get conjoin data from assets/test/ folder
   * @param 
   * @returns 
   */
  async getTestConjoint2(): Promise<Survey> {
    return await firstValueFrom(this.http.get<Survey>("assets/test/survey_conjoint_2.json"));
  }

  /**
   * @author Matías Villar
   * @date 06-10-2022
   * @desription check if uuid_survey exists
   * @param uuid_survey 
   * @returns 
   */
  public async evaluateUuidSurvey(uuid_survey: string, api_domain: string, is_preview: boolean, uuid_respondent: string): Promise<Survey> {
    this.uuid4 = uuid_survey
    let result: Survey
    let test = this.json_test[this.uuid4]
    if (test != undefined) result = await this.getJSONTestNew(test)
    else
      return new Promise(async (resolve, reject) => {
          //if is_preview call endpoint of template(pauta)
          if (is_preview) {
            this.firebaseService.callFunction<Survey>('getSurveyTemplateProperties', { "api_domain": api_domain, "uuid": uuid_survey, 'respondent_uuid': uuid_respondent }).then((response) =>{
              result = response.data
              resolve(result)
            }).catch( async (error) => {
              try {
                const construct_survey = await this.getSurveyFromStorageSafe(uuid_survey);
                if (construct_survey) {
                  result = construct_survey;
                  resolve(result);
                } else {
                  return reject(error);
                }
              } catch (storageError) {
                console.error('Failed to get survey from storage:', storageError);
                return reject(error);
              }
            })
          } else {
            this.firebaseService.callFunction<Survey>('getSurveyProperties', { "api_domain": api_domain, "uuid": uuid_survey, 'respondent_uuid': uuid_respondent }).then((response) =>{
              result = response.data
              resolve(result)
            }).catch( async (error) => {
              try {
                const surveyKey = this.networkService.getStatus() ? uuid_survey : uuid_survey + '_offline';
                const construct_survey = await this.getSurveyFromStorageSafe(surveyKey);
                if (construct_survey) {
                  result = construct_survey;
                  resolve(result);
                } else {
                  return reject(error);
                }
              } catch (storageError) {
                console.error('Failed to get survey from storage:', storageError);
                return reject(error);
              }
            })
          }
      })

  }

  /**
   * @author Matías Villar
   * @date 06-10-2022
   * @desription get Json Test
   * @param example_url 
   * @returns 
   */
  private getJSONTestNew(example_url: string) {
    return firstValueFrom(this.http.get<Survey>(example_url));
  }

  private getSurveyFromStorageSafe(surveyId: string): Promise<Survey | null> {
    return getSurveyFromStorageWithFallback(this.storageManagerService, this.localStorageService, surveyId);
  }
  private getHeaders() {
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'Authorization': environment.qsToken
      })
    };

    return httpOptions;
  }
  /**
   * get sensors from user account
   */
  getSensors() {
    const now = Date.now();
    if (this.sensorsCache$ && now < this.sensorsCacheExpiry) {
      return firstValueFrom(this.sensorsCache$);
    }

    const request$ = this.http
      .get(this.apiQSSUrl + 'sensors/', this.getHeaders())
      .pipe(shareReplay(1));
    this.sensorsCache$ = request$;
    this.sensorsCacheExpiry = now + this.sensorsCacheTtlMs;
    return firstValueFrom(request$);
  }
  /**
   * Get survey list available for user account
   * @author Daniel García
   * @date 24-02-2023
   * @param api_domain
   * @return Promise
   */
  getSurveys(api_domain: string) {
    type SurveysResponse = Record<string, unknown>;
    return new Promise(async (resolve, reject) => {
      const fnRes = await this.firebaseService.callFunction<SurveysResponse>('getSurveys', { "email": this.authService.user_data.email, "api_domain": api_domain, "range": "?limit=25&offset=0" })
      resolve(fnRes.data)
    })
  }
  /**
   * Get next survey list page
   * @author Daniel García
   * @date 24-02-2023
   * @param api_domain server
   * @param url to get URL parameters
   * @return Promise
   */
  getNextPageSurveys(api_domain: string, url: string) {
    const range = `?${url.split("?")[1]}`;
    type SurveysResponse = Record<string, unknown>;
    return new Promise(async (resolve, reject) => {
      const fnRes = await this.firebaseService.callFunction<SurveysResponse>('getSurveys', { "email": this.authService.user_data.email, "api_domain": api_domain, "range": range })
      resolve(fnRes.data)
    })
  }
  /**
   * Send token to validate app with QServus
   * @author Daniel García
   * @date 24-02-2023
   * @param api_domain server
   * @param token recatpcha token
   * @return Promise
   */
  validateCaptcha(api_domain: string, token: string) {
    type CaptchaResponse = Record<string, unknown>;
    return new Promise(async (resolve, reject) => {
      const fnRes = await this.firebaseService.callFunction<CaptchaResponse>('validateRecaptcha', { "api_domain": api_domain, "token": token })
      resolve(fnRes.data)
    })
  }
  /**
   * validate if code is correct or not (must be asociated with phone number)
   * @param code validation code sended to client by sms
   * @param phone client phone number
   */
  validateCodeSMS(code: string, phone: string) {
    return firstValueFrom(this.http.post(this.apiQSSUrl + 'validate-sms-code/', { "code": code, "phone": phone }, this.getHeaders()));
  }
  /**
   * Get access token for endpoints with bearer authentication
   * @returns 
   */
  getAccessToken(): Promise<any> {
    const credentials = {
      "email": environment.qservusGlobalAuth.user,
      "password": environment.qservusGlobalAuth.pass,
    }
    return firstValueFrom(this.http.post(environment.qsApiUrl + "token/", credentials));
  }
  /**
   * send responses to QServus
   * @author Daniel García
   * @date 24-02-2023
   * @param formated_answer answers to process in QServus API
   * @returns Promise
   */
  sendAnswer(formated_answer: any): Promise<any> {
    type SendAnswerResponse = Record<string, unknown>;
    return new Promise(async (resolve, reject) => {
      const fnRes = await this.firebaseService.callFunction<SendAnswerResponse>('sendAnswers', { "api_domain": this.api_domain, "formated_answer": formated_answer })
      resolve(fnRes.data)
    })
  }
  /**
   * Process partial answers in QServus
   * @author Daniel García
   * @date 24-02-2023
   * @param answers 
   * @param api_domain 
   * @returns 
   */
  sendPartialAnswer(formated_answer: any): Promise<any> {
    type SendPartialAnswerResponse = Record<string, unknown>;
    return new Promise(async (resolve, reject) => {
      const fnRes = await this.firebaseService.callFunction<SendPartialAnswerResponse>('sendPartialAnswers', { "api_domain": this.api_domain, "formated_answer": formated_answer })
      resolve(fnRes.data)
    })
  }
  /**
   * check if otp code is correct
   * @author Daniel García
   * @date 24-02-2023
   * @param respondent_id 
   * @param otp_code 
   * @returns 
   */
  async validateOTP(respondent_uuid: string, body_request: any, api_domain: string, otp_code?: string) {

    const data = {
      api_domain: api_domain,
      uuid: respondent_uuid,
      body_request: body_request
    }
    if (otp_code != undefined) data['otp_code'] = otp_code

    type ValidateOtpResponse = Record<string, unknown>;
    return new Promise(async (resolve, reject) => {
      const fnRes = await this.firebaseService.callFunction<ValidateOtpResponse>('validateOTP', data)
      resolve(fnRes.data)
    })
  }
  /**
   * Send manual OTP notification to client (email notification)
   * @author Daniel García
   * @date 24-02-2023
   * @param respondent_uuid 
   * @param email 
   */
  async manualNotificationOTP(respondent_uuid: string, email: string) {
    type ManualOtpResponse = Record<string, unknown>;
    return new Promise(async (resolve, reject) => {
      const fnRes = await this.firebaseService.callFunction<ManualOtpResponse>('manualOTPNotification', {
        "uuid": respondent_uuid,
        "email": email,
        "api_domain": this.api_domain
      })
      resolve(fnRes.data)
    })
  }
  /**
   * @author Matías Villar
   * @date 06-12-2022
   * @desription Get partial answers from survey with respondent.
   * @param uuid_respondent 
   * @returns 
   */
  public getPartialAnswers(uuid_respondent: string, api_domain: string): Promise<Partial_answers> {
    return new Promise(async (resolve, reject) => {
      const fnRes = await this.firebaseService.callFunction<Partial_answers>('getPartialAnswers', { "uuid_respondent": uuid_respondent, "api_domain": api_domain })
      resolve(fnRes.data)
    })
    // return this.http.get<Partial_answers>(`https://${api_domain}/api/qss/partial-answers/${uuid_respondent}/`, this.getHeaders()).toPromise()
  }

  /**
   * @author Matías Villar
   * @date 06-12-2022
   * @desription Get partial answers from survey with respondent.
   * @param uuid_respondent 
   * @returns 
   */
  public getOtpCode(uuid_respondent: string, api_domain: string): Promise<any> {
    type OtpCodeResponse = Record<string, unknown>;
    return new Promise(async (resolve, reject) => {
      const fnRes = await this.firebaseService.callFunction<OtpCodeResponse>('getOtpCode', { "uuid_respondent": uuid_respondent, "api_domain": api_domain })
      resolve(fnRes.data)
    })
  }
}
